import { useEffect, useMemo, useState } from 'react';
import { Edit2, MoreVertical, Settings, Activity } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/Card';
import { NoGroupsMessage } from '../components/NoGroupsMessage';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { apiClient } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { Button } from '../components/ui/Button';
import { getTenantColor } from '../lib/colors';
import { MonitoringConfigEditDialog } from '../components/monitoring/MonitoringConfigEditDialog';
import { MonitoringServicesDialog } from '../components/monitoring/MonitoringServicesDialog';
import { Skeleton } from '../components/ui/Skeleton';
import { PageHeader } from '../components/ui/PageHeader';
import { TenantLogo } from '../components/tenants/TenantLogo';
import { resolveTenantMeta, useTenantMetaMap } from '../hooks/useTenantMetaMap';

export function Monitoring() {
  const { user, accessibleTenants, selectedTenants } = useAuth();
  const { isAllowed: canView } = usePermission('monitoring', 'read');
  const { isAllowed: canManage } = usePermission('monitoring', 'manage');
  const tenantMetaMap = useTenantMetaMap(canView);

  const hasNoGroups = user && (!user.groups || user.groups.length === 0);
  if (hasNoGroups) return <NoGroupsMessage />;

  const tenantsToQuery = useMemo(() => {
    const list = (selectedTenants && selectedTenants.length > 0) ? selectedTenants : accessibleTenants;
    return (list || []).map((t) => String(t).toLowerCase());
  }, [accessibleTenants, selectedTenants]);

  const [tenants, setTenants] = useState<Array<{ code: string; name: string; color?: string | null }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [showMasterEditor, setShowMasterEditor] = useState(false);

  const [tenantConfigs, setTenantConfigs] = useState<Record<string, any>>({});
  const [tenantClusters, setTenantClusters] = useState<Record<string, any[]>>({});
  const [tenantEditorTenant, setTenantEditorTenant] = useState<string | null>(null);
  const [tenantEditorPrefill, setTenantEditorPrefill] = useState<((currentMonitoring: any) => any) | undefined>(undefined);
  const [servicesTenant, setServicesTenant] = useState<string | null>(null);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setActionMsg(null);
    void (async () => {
      try {
        const tenantsResponse = await apiClient.getTenants();
        const tenantsArr = (Array.isArray(tenantsResponse) ? tenantsResponse : tenantsResponse.tenants || []) as any[];
        const wanted = new Set((tenantsToQuery || []).map((t) => String(t).toLowerCase()));
        const filtered = tenantsArr
          .map((t: any) => ({ code: String(t.code || '').toLowerCase(), name: t.name, color: t.color }))
          .filter((t: any) => wanted.has(String(t.code).toLowerCase()));
        if (!cancelled) setTenants(filtered);
      } catch (e: any) {
        if (cancelled) return;
        setError(e?.message || 'Failed to load monitoring config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(',')]);

  // Background-load configs for tenants in view.
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    void (async () => {
      const missing = (tenantsToQuery || []).filter((t) => !tenantConfigs[t]);
      if (missing.length === 0) return;
      try {
        const results = await Promise.all(
          missing.map(async (t) => {
            try {
              const res = await apiClient.getMonitoringTenantConfig(t);
              return { t, cfg: res?.monitoring || {} };
            } catch {
              return { t, cfg: null };
            }
          })
        );
        if (cancelled) return;
        setTenantConfigs((prev) => {
          const next = { ...prev };
          for (const r of results) {
            if (r.cfg !== null) next[r.t] = r.cfg;
          }
          return next;
        });
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView, tenantsToQuery.join(','), tenantConfigs]);

  // Background-load OCI cluster status for Alertmanager badge
  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await apiClient.getOpenshiftClusters();
        const clusters = response?.clusters || [];
        if (cancelled) return;
        
        // Group clusters by tenant
        const grouped: Record<string, any[]> = {};
        for (const cluster of clusters) {
          const tc = String(cluster.tenantCode || '').toLowerCase();
          if (!grouped[tc]) grouped[tc] = [];
          grouped[tc].push(cluster);
        }
        setTenantClusters(grouped);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [canView]);

  if (!canView) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Monitoring</CardTitle>
        </CardHeader>
        <CardContent>You don&apos;t have permission to view Monitoring.</CardContent>
      </Card>
    );
  }

  const openTenantEditor = (tenantCode: string, prefill?: (currentMonitoring: any) => any) => {
    setTenantEditorPrefill(() => prefill);
    setTenantEditorTenant(tenantCode);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Activity}
        iconColor="text-orange-600 dark:text-orange-400"
        iconBgColor="bg-orange-100 dark:bg-orange-900/30"
        description="Monitor tenant configurations and integrations"
        count={tenants.length}
        countLabel="tenants"
        countColor="text-orange-600 dark:text-orange-400"
        actions={
          canManage ? (
            <div className="w-full sm:w-auto sm:h-full flex items-stretch">
              <DropdownMenu
                className="w-full h-full"
                trigger={
                  <Button variant="outline" size="sm" className="w-full h-full text-sm py-2">
                    <Settings className="w-4 h-4 mr-2" />
                    Actions
                  </Button>
                }
                items={[
                  {
                    label: 'Edit Master Monitoring Config',
                    icon: <Edit2 className="w-4 h-4" />,
                    onClick: () => setShowMasterEditor(true),
                  },
                ]}
              />
            </div>
          ) : undefined
        }
      />

      {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
      {actionMsg && <div className="text-sm text-green-700 dark:text-green-400">{actionMsg}</div>}

      {loading && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <Skeleton variant="circle" className="w-10 h-10" />
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-24" />
                      <Skeleton className="h-3 w-16" />
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-8" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Skeleton className="h-6 w-16" />
                    <Skeleton className="h-6 w-20" />
                    <Skeleton className="h-6 w-12" />
                    <Skeleton className="h-6 w-16" />
                  </div>
                  <Skeleton className="h-4 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {tenants.length === 0 && !loading && !error ? (
        <Card>
          <CardContent className="py-8 text-center text-gray-500 dark:text-gray-400">
            No tenants found for current selection.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {tenants.map((tenant) => {
            const tenantMeta = resolveTenantMeta(tenantMetaMap, tenant.code, tenant);
            const cfg = tenantConfigs[tenant.code] || {};
            const clusters = tenantClusters[tenant.code] || [];
            const hasOpenShiftClusters = clusters.length > 0; // Any cloud provider (OCI, AWS, Hetzner)
            
            const status = {
              uptime: Array.isArray(cfg?.services) && cfg.services.length > 0,
              alertmanager: hasOpenShiftClusters && (
                !!cfg?.alertmanager?.base_url || 
                !!cfg?.alertmanager?.openshift?.instanceName ||
                (cfg?.alertmanager?.environments && Object.keys(cfg.alertmanager.environments).length > 0)
              ),
              jira: !!(cfg?.jira?.enabled === true && cfg?.jira?.projectKey),
              synthetic: Array.isArray(cfg?.synthetic?.suites) && cfg.synthetic.suites.length > 0,
              grafana: cfg?.grafana?.enabled === true,
            };
            const configuredCount = [status.uptime, status.alertmanager, status.jira, status.synthetic, status.grafana].filter(Boolean).length;
            const servicesCount = Array.isArray(cfg?.services) ? cfg.services.length : 0;
            const servicesConfigured = servicesCount > 0;

            const ensureCategoryPrefill = (cat: 'alertmanager' | 'jira' | 'synthetic') => {
              return (current: any) => {
                const next = { ...(current || {}) };
                if (cat === 'alertmanager') next.alertmanager = next.alertmanager || { base_url: '', api_token_ref: '', openshift: { cloudProvider: 'Hetzner', instanceName: '' }, labelMap: {} };
                if (cat === 'jira') next.jira = next.jira || { projectKey: '', jql: {} };
                if (cat === 'synthetic') next.synthetic = next.synthetic || { suites: [] };
                return next;
              };
            };

            return (
              <Card key={tenant.code} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <TenantLogo
                        tenant={tenantMeta}
                        size="md"
                        rounded="lg"
                        className="border border-gray-200/80 dark:border-gray-700/80"
                      />
                      <div>
                        <CardTitle className="text-lg" style={{ color: getTenantColor(tenantMeta) }}>
                          {tenantMeta.name}
                        </CardTitle>
                        <CardDescription className="font-mono text-xs mt-1">
                          {tenantMeta.code}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      {(() => {
                        const items = [
                          canManage
                            ? {
                                label: 'Edit Monitoring Config',
                                icon: <Edit2 className="w-4 h-4" />,
                                onClick: () => openTenantEditor(tenant.code),
                              }
                            : null,
                          canManage
                            ? {
                                label: 'Manage Services',
                                icon: <Edit2 className="w-4 h-4" />,
                                onClick: () => setServicesTenant(tenant.code),
                              }
                            : null,
                        ].filter(Boolean) as any[];

                        if (items.length === 0) return null;

                        return (
                          <DropdownMenu
                            trigger={
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            }
                            items={items}
                          />
                        );
                      })()}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm text-gray-600 dark:text-gray-400">Sources configured</div>
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      {configuredCount}/5
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    <Badge variant={status.uptime ? 'success' : 'outline'}>Uptime</Badge>
                    <Badge variant={status.alertmanager ? 'success' : 'outline'}>Alertmanager</Badge>
                    <Badge variant={status.jira ? 'success' : 'outline'}>Jira</Badge>
                    <Badge variant={status.synthetic ? 'success' : 'outline'}>Synthetic</Badge>
                    <Badge variant={status.grafana ? 'success' : 'outline'}>Grafana Webhook</Badge>
                  </div>

                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant={servicesConfigured ? 'success' : 'outline'}>Services</Badge>
                        <span>
                          <span className="font-medium text-gray-900 dark:text-gray-100">{servicesCount}</span>
                          {!servicesConfigured && <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(not configured)</span>}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <MonitoringConfigEditDialog
        open={showMasterEditor}
        onClose={() => setShowMasterEditor(false)}
        onSuccess={async () => {
          // Clear tenant snapshots; UI will re-poll and reflect any master-level changes.
          setTenantConfigs({});
        }}
        isMaster
      />

      <MonitoringConfigEditDialog
        open={!!tenantEditorTenant}
        tenantCode={tenantEditorTenant}
        onClose={() => setTenantEditorTenant(null)}
        onSuccess={async () => {
          const tc = String(tenantEditorTenant || '').toLowerCase();
          if (!tc) return;
          try {
            const res = await apiClient.getMonitoringTenantConfig(tc);
            setTenantConfigs((prev) => ({ ...prev, [tc]: res?.monitoring || {} }));
          } catch {
            // ignore
          }
        }}
        prefill={tenantEditorPrefill}
      />

      <MonitoringServicesDialog
        open={!!servicesTenant}
        tenantCode={servicesTenant}
        onClose={() => setServicesTenant(null)}
        onSaved={(tc, newMonitoring) => {
          setTenantConfigs((prev) => ({ ...prev, [tc]: newMonitoring || {} }));
        }}
      />
    </div>
  );
}
